import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import axios from 'axios';
import classNames from 'classnames';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { toast } from 'react-toastify';
import {
  Alert,
  Badge,
  Button,
  Card,
  CardBody,
  Col,
  Form,
  FormGroup,
  Input,
  Label,
  Modal,
  ModalBody,
  ModalFooter,
  ModalHeader,
  Row,
  Spinner
} from 'reactstrap';
import TablePageSizeSelect from '../common/TablePageSizeSelect';
import MedDataTable from '../common/MedDataTable';
import QuillEditor from '../common/QuillEditor';
import useStoredTablePageSize from '../../hooks/useStoredTablePageSize';
import apiBase from '../../apiConfig';
import { emitResultTopicsUpdate } from '../../helpers/resultTopics';
import { getAuthHeaders } from '../../helpers/utils';

const BASE_ENDPOINT = `${apiBase}/p-admin/tests.php`;
const OVERVIEW_ENDPOINT = `${BASE_ENDPOINT}?action=overview`;
const TOPIC_ENDPOINT = `${BASE_ENDPOINT}?action=topic`;
const CREATE_TOPIC_ENDPOINT = `${BASE_ENDPOINT}?action=create-topic`;
const RENAME_TOPIC_ENDPOINT = `${BASE_ENDPOINT}?action=rename-topic`;
const SAVE_QUESTION_ENDPOINT = `${BASE_ENDPOINT}?action=save-question`;
const QUESTION_ARCHIVE_ENDPOINT = `${BASE_ENDPOINT}?action=question-archive`;
const TOPIC_ARCHIVE_ENDPOINT = `${BASE_ENDPOINT}?action=topic-archive`;
const IMAGE_UPLOAD_ENDPOINT = `${BASE_ENDPOINT}?action=upload-inline-image`;
const MAX_IMAGE_SIZE = 3 * 1024 * 1024;

const EMPTY_OVERVIEW = {
  stats: {
    topic_count: 0,
    active_topic_count: 0,
    question_count: 0,
    archived_question_count: 0,
    image_question_count: 0
  },
  topics: []
};

const EMPTY_TOPIC_DETAIL = {
  topic: null,
  questions: []
};

const QUESTION_EDITOR_FORMATS = [
  'header',
  'bold',
  'italic',
  'underline',
  'strike',
  'blockquote',
  'list',
  'bullet',
  'indent',
  'link',
  'image',
  'color',
  'background',
  'align'
];

const VARIANT_FIELDS = [
  {
    name: 'correct_answer',
    label: 'A variant',
    title: 'To`g`ri javob',
    note: 'Asosiy javob shu maydonda turadi.',
    tone: 'answer'
  },
  {
    name: 'option_1',
    label: 'B variant',
    title: 'Qo`shimcha variant',
    note: 'Ikkinchi ko`rinish uchun variant.',
    tone: 'secondary'
  },
  {
    name: 'option_2',
    label: 'C variant',
    title: 'Qo`shimcha variant',
    note: 'Uchinchi ko`rinish uchun variant.',
    tone: 'accent'
  },
  {
    name: 'option_3',
    label: 'D variant',
    title: 'Qo`shimcha variant',
    note: 'To`rtinchi ko`rinish uchun variant.',
    tone: 'muted'
  }
];

const createQuestionForm = (topicKey = '', topicTitle = '') => ({
  test_savol_id: null,
  topic_key: topicKey,
  topic_title: topicTitle,
  question_html: '',
  correct_answer: '',
  option_1: '',
  option_2: '',
  option_3: ''
});

const extractErrorMessage = (error, fallback) =>
  error?.response?.data?.message || error?.message || fallback;

const topicStatusMeta = topic =>
  topic?.is_archived
    ? {
        label: 'Arxiv mavzu',
        badgeClass: 'med-testlab-status med-testlab-status--archived'
      }
    : {
        label: 'Faol mavzu',
        badgeClass: 'med-testlab-status med-testlab-status--active'
      };

const questionStatusMeta = archived =>
  archived
    ? {
        label: 'Arxivda',
        badgeClass: 'med-testlab-status med-testlab-status--archived'
      }
    : {
        label: 'Faol',
        badgeClass: 'med-testlab-status med-testlab-status--active'
      };

const TestBuilderSettings = () => {
  const [overview, setOverview] = useState(EMPTY_OVERVIEW);
  const [permissions, setPermissions] = useState({ view: false, manage: false });
  const [loading, setLoading] = useState(true);
  const [banner, setBanner] = useState(null);
  const [topicSearch, setTopicSearch] = useState('');
  const [topicStatusFilter, setTopicStatusFilter] = useState('all');
  const [selectedTopicKey, setSelectedTopicKey] = useState('');
  const [topicDetail, setTopicDetail] = useState(EMPTY_TOPIC_DETAIL);
  const [topicModalOpen, setTopicModalOpen] = useState(false);
  const [topicModalLoading, setTopicModalLoading] = useState(false);
  const [questionModalOpen, setQuestionModalOpen] = useState(false);
  const [createTopicModalOpen, setCreateTopicModalOpen] = useState(false);
  const [renameTopicModalOpen, setRenameTopicModalOpen] = useState(false);
  const [topicDraftTitle, setTopicDraftTitle] = useState('');
  const [renameTopicTitle, setRenameTopicTitle] = useState('');
  const [creatingTopic, setCreatingTopic] = useState(false);
  const [renamingTopic, setRenamingTopic] = useState(false);
  const [questionSearch, setQuestionSearch] = useState('');
  const [pageSize, setPageSize] = useStoredTablePageSize();
  const [questionForm, setQuestionForm] = useState(createQuestionForm());
  const [savingQuestion, setSavingQuestion] = useState(false);
  const [uploadingImage, setUploadingImage] = useState(false);

  const editorRef = useRef(null);
  const imageInputRef = useRef(null);
  const pendingSelectionRef = useRef(null);

  const authHeaders = useMemo(() => getAuthHeaders(), []);
  const requestConfig = useMemo(
    () => ({
      headers: authHeaders
    }),
    [authHeaders]
  );
  const jsonRequestConfig = useMemo(
    () => ({
      headers: {
        ...authHeaders,
        'Content-Type': 'application/json'
      }
    }),
    [authHeaders]
  );

  const canManage = Boolean(permissions.manage);
  const topics = useMemo(() => overview.topics || [], [overview.topics]);

  const applyOverview = useCallback(payload => {
    const data = payload || EMPTY_OVERVIEW;
    emitResultTopicsUpdate(data.topics || []);
    setOverview({
      stats: data.stats || EMPTY_OVERVIEW.stats,
      topics: data.topics || []
    });
  }, []);

  const resetQuestionForm = useCallback((topicKey = '', topicTitle = '') => {
    setQuestionForm(createQuestionForm(topicKey, topicTitle));
  }, []);

  const loadOverview = useCallback(
    async (silent = false) => {
      if (!silent) {
        setLoading(true);
      }

      try {
        const response = await axios.get(OVERVIEW_ENDPOINT, requestConfig);

        if (response.data?.success) {
          applyOverview(response.data.data || EMPTY_OVERVIEW);
          setPermissions(response.data.permissions || { view: false, manage: false });
          return response.data.data || EMPTY_OVERVIEW;
        }

        throw new Error(response.data?.message || 'Mavzular yuklanmadi.');
      } catch (error) {
        setBanner({
          color: 'danger',
          text: extractErrorMessage(error, 'Custom test mavzularini yuklashda xatolik yuz berdi.')
        });
        return null;
      } finally {
        if (!silent) {
          setLoading(false);
        }
      }
    },
    [applyOverview, requestConfig]
  );

  const loadTopic = useCallback(
    async topicKey => {
      if (!topicKey) {
        setSelectedTopicKey('');
        setTopicDetail(EMPTY_TOPIC_DETAIL);
        return null;
      }

      setTopicModalLoading(true);

      try {
        const response = await axios.get(TOPIC_ENDPOINT, {
          ...requestConfig,
          params: { kalit: topicKey }
        });

        if (response.data?.success) {
          const nextDetail = response.data.data || EMPTY_TOPIC_DETAIL;
          setSelectedTopicKey(topicKey);
          setTopicDetail(nextDetail);
          return nextDetail;
        }

        throw new Error(response.data?.message || 'Mavzu ma`lumotlari yuklanmadi.');
      } catch (error) {
        toast.error(extractErrorMessage(error, 'Mavzu ma`lumotlarini yuklashda xatolik yuz berdi.'));
        return null;
      } finally {
        setTopicModalLoading(false);
      }
    },
    [requestConfig]
  );

  useEffect(() => {
    loadOverview();
  }, [loadOverview]);

  const filteredTopics = useMemo(() => {
    const search = topicSearch.trim().toLowerCase();

    return topics.filter(topic => {
      if (topicStatusFilter === 'active' && topic.is_archived) {
        return false;
      }

      if (topicStatusFilter === 'archived' && !topic.is_archived) {
        return false;
      }

      if (!search) {
        return true;
      }

      return [topic.title, topic.key, topic.preview, topic.created_by].some(value =>
        String(value || '')
          .toLowerCase()
          .includes(search)
      );
    });
  }, [topicSearch, topicStatusFilter, topics]);

  const filteredQuestions = useMemo(() => {
    const search = questionSearch.trim().toLowerCase();

    return (topicDetail.questions || []).filter(question => {
      if (!search) {
        return true;
      }

      return [
        question.question_text,
        question.correct_answer,
        ...(question.options || []),
        question.author
      ].some(value =>
        String(value || '')
          .toLowerCase()
          .includes(search)
      );
    });
  }, [questionSearch, topicDetail.questions]);

  const activeTopic = useMemo(() => {
    if (topicDetail.topic && topicDetail.topic.key === selectedTopicKey) {
      return topicDetail.topic;
    }

    return topics.find(item => item.key === selectedTopicKey) || topicDetail.topic || null;
  }, [selectedTopicKey, topicDetail.topic, topics]);

  const activeTopicTitle = activeTopic ? activeTopic.title : '';

  const openTopicModal = useCallback(
    async topic => {
      if (!topic?.key) {
        return;
      }

      setQuestionModalOpen(false);
      setRenameTopicModalOpen(false);
      setTopicModalOpen(true);
      setQuestionSearch('');
      setSelectedTopicKey(topic.key);
      resetQuestionForm(topic.key, topic.title);
      await loadTopic(topic.key);
    },
    [loadTopic, resetQuestionForm]
  );

  const closeTopicModal = useCallback(() => {
    setQuestionModalOpen(false);
    setRenameTopicModalOpen(false);
    setTopicModalOpen(false);
    setQuestionSearch('');
    if (activeTopic) {
      resetQuestionForm(activeTopic.key, activeTopic.title);
    } else {
      resetQuestionForm();
    }
  }, [activeTopic, resetQuestionForm]);

  const closeQuestionModal = useCallback(() => {
    setRenameTopicModalOpen(false);
    setQuestionModalOpen(false);
    if (activeTopic) {
      resetQuestionForm(activeTopic.key, activeTopic.title);
    } else {
      resetQuestionForm();
    }
  }, [activeTopic, resetQuestionForm]);

  const openRenameTopicModal = useCallback(() => {
    if (!activeTopic || !canManage) {
      return;
    }

    setRenameTopicTitle(activeTopic.title || '');
    setRenameTopicModalOpen(true);
  }, [activeTopic, canManage]);

  const closeRenameTopicModal = useCallback(() => {
    setRenameTopicModalOpen(false);
    setRenameTopicTitle('');
  }, []);

  const openNewQuestionModal = useCallback(() => {
    if (!activeTopic) {
      return;
    }

    resetQuestionForm(activeTopic.key, activeTopic.title);
    setQuestionModalOpen(true);
  }, [activeTopic, resetQuestionForm]);

  const startEditQuestion = useCallback(question => {
    if (!question) {
      return;
    }

    setQuestionForm({
      test_savol_id: question.id,
      topic_key: question.topic_key,
      topic_title: question.topic_title,
      question_html: question.question_html || '',
      correct_answer: question.correct_answer || '',
      option_1: question.option_1 || '',
      option_2: question.option_2 || '',
      option_3: question.option_3 || ''
    });
    setQuestionModalOpen(true);
  }, []);

  const handleQuestionFormChange = useCallback(event => {
    const { name, value } = event.target;
    setQuestionForm(current => ({
      ...current,
      [name]: value
    }));
  }, []);

  const handleEditorChange = useCallback(value => {
    setQuestionForm(current => ({
      ...current,
      question_html: value
    }));
  }, []);

  const openImagePicker = useCallback(() => {
    if (!canManage || !questionModalOpen) {
      return;
    }

    const editor = editorRef.current?.getEditor?.();
    pendingSelectionRef.current = editor?.getSelection?.(true) || null;

    if (imageInputRef.current) {
      imageInputRef.current.click();
    }
  }, [canManage, questionModalOpen]);

  const handleEditorImagePick = useCallback(
    async event => {
      const file = event.target.files?.[0];
      event.target.value = '';

      if (!file) {
        return;
      }

      if (file.size > MAX_IMAGE_SIZE) {
        toast.error('Rasm hajmi 3 MB dan oshmasligi kerak.');
        return;
      }

      setUploadingImage(true);

      try {
        const payload = new FormData();
        payload.append('image', file);
        payload.append('topic_key', selectedTopicKey || questionForm.topic_key || '');
        payload.append('topic_title', activeTopicTitle || questionForm.topic_title || '');

        const response = await axios.post(IMAGE_UPLOAD_ENDPOINT, payload, requestConfig);

        if (!response.data?.success || !response.data.image_url) {
          throw new Error(response.data?.message || 'Rasmni editorga yuklab bo`lmadi.');
        }

        const editor = editorRef.current?.getEditor?.();
        if (!editor) {
          throw new Error('Editor topilmadi.');
        }

        const range = pendingSelectionRef.current || editor.getSelection(true);
        const index = range?.index ?? editor.getLength();
        editor.insertEmbed(index, 'image', response.data.image_url, 'user');
        editor.setSelection(index + 1, 0, 'silent');

        setQuestionForm(current => ({
          ...current,
          question_html: editor.root.innerHTML
        }));

        toast.success('Rasm editor ichiga joylandi.');
      } catch (error) {
        toast.error(extractErrorMessage(error, 'Rasmni yuklashda xatolik yuz berdi.'));
      } finally {
        pendingSelectionRef.current = null;
        setUploadingImage(false);
      }
    },
    [activeTopicTitle, questionForm.topic_key, questionForm.topic_title, requestConfig, selectedTopicKey]
  );

  const editorModules = useMemo(
    () => ({
      toolbar: {
        container: [
          [{ header: [2, 3, false] }],
          ['bold', 'italic', 'underline', 'blockquote'],
          [{ list: 'ordered' }, { list: 'bullet' }],
          [{ color: [] }, { background: [] }, { align: [] }],
          ['link', 'image'],
          ['clean']
        ],
        handlers: {
          image: openImagePicker
        }
      },
      clipboard: { matchVisual: false }
    }),
    [openImagePicker]
  );

  const handleCreateTopic = useCallback(
    async event => {
      event.preventDefault();

      if (!canManage) {
        return;
      }

      setCreatingTopic(true);

      try {
        const response = await axios.post(
          CREATE_TOPIC_ENDPOINT,
          {
            topic_title: topicDraftTitle
          },
          jsonRequestConfig
        );

        if (!response.data?.success) {
          throw new Error(response.data?.message || 'Mavzuni yaratib bo`lmadi.');
        }

        if (response.data.overview) {
          applyOverview(response.data.overview);
        } else {
          await loadOverview(true);
        }

        const nextDetail = response.data.data || EMPTY_TOPIC_DETAIL;
        const nextTopic = nextDetail.topic || null;
        setTopicDraftTitle('');
        setCreateTopicModalOpen(false);
        setTopicDetail(nextDetail);

        if (nextTopic?.key) {
          setSelectedTopicKey(nextTopic.key);
          setTopicModalOpen(true);
          resetQuestionForm(nextTopic.key, nextTopic.title);
        }

        toast.success(response.data.message || 'Mavzu yaratildi.');
      } catch (error) {
        toast.error(extractErrorMessage(error, 'Mavzuni yaratishda xatolik yuz berdi.'));
      } finally {
        setCreatingTopic(false);
      }
    },
    [applyOverview, canManage, jsonRequestConfig, loadOverview, resetQuestionForm, topicDraftTitle]
  );

  const handleRenameTopic = useCallback(
    async event => {
      event.preventDefault();

      if (!canManage || !activeTopic?.key) {
        return;
      }

      setRenamingTopic(true);

      try {
        const response = await axios.post(
          RENAME_TOPIC_ENDPOINT,
          {
            topic_key: activeTopic.key,
            topic_title: renameTopicTitle
          },
          jsonRequestConfig
        );

        if (!response.data?.success) {
          throw new Error(response.data?.message || 'Mavzu nomini yangilab bo`lmadi.');
        }

        if (response.data.overview) {
          applyOverview(response.data.overview);
        } else {
          await loadOverview(true);
        }

        if (response.data.data) {
          setTopicDetail(response.data.data);
        }

        setQuestionForm(current => ({
          ...current,
          topic_title: renameTopicTitle
        }));
        setRenameTopicModalOpen(false);
        toast.success(response.data.message || 'Mavzu nomi yangilandi.');
      } catch (error) {
        toast.error(extractErrorMessage(error, 'Mavzu nomini yangilashda xatolik yuz berdi.'));
      } finally {
        setRenamingTopic(false);
      }
    },
    [activeTopic, applyOverview, canManage, jsonRequestConfig, loadOverview, renameTopicTitle]
  );

  const handleSaveQuestion = useCallback(
    async event => {
      event.preventDefault();

      if (!canManage || !activeTopic?.key || activeTopic.is_archived) {
        return;
      }

      setSavingQuestion(true);
      setBanner(null);

      try {
        const payload = new FormData();
        payload.append('test_savol_id', questionForm.test_savol_id || '');
        payload.append('topic_key', activeTopic.key);
        payload.append('topic_title', activeTopic.title || questionForm.topic_title || '');
        payload.append('question_html', questionForm.question_html || '');
        payload.append('correct_answer', questionForm.correct_answer || '');
        payload.append('option_1', questionForm.option_1 || '');
        payload.append('option_2', questionForm.option_2 || '');
        payload.append('option_3', questionForm.option_3 || '');

        const response = await axios.post(SAVE_QUESTION_ENDPOINT, payload, requestConfig);

        if (!response.data?.success) {
          throw new Error(response.data?.message || 'Savolni saqlab bo`lmadi.');
        }

        if (response.data.overview) {
          applyOverview(response.data.overview);
        } else {
          await loadOverview(true);
        }

        const nextDetail = response.data.data || EMPTY_TOPIC_DETAIL;
        const nextTopic = nextDetail.topic || activeTopic;
        setTopicDetail(nextDetail);
        resetQuestionForm(nextTopic.key, nextTopic.title);

        toast.success(response.data.message || 'Savol saqlandi.');
      } catch (error) {
        const message = extractErrorMessage(error, 'Savolni saqlashda xatolik yuz berdi.');
        setBanner({ color: 'danger', text: message });
        toast.error(message);
      } finally {
        setSavingQuestion(false);
      }
    },
    [activeTopic, applyOverview, canManage, loadOverview, questionForm, requestConfig, resetQuestionForm]
  );

  const handleTopicArchive = useCallback(
    async (topic, archiveValue) => {
      if (!topic || !canManage) {
        return;
      }

      const confirmed = window.confirm(
        archiveValue === 1 ? 'Mavzuni arxivlashni xohlaysizmi?' : 'Mavzuni qayta ochishni xohlaysizmi?'
      );

      if (!confirmed) {
        return;
      }

      try {
        const response = await axios.post(
          TOPIC_ARCHIVE_ENDPOINT,
          {
            topic_key: topic.key,
            arxiv: archiveValue
          },
          jsonRequestConfig
        );

        if (!response.data?.success) {
          throw new Error(response.data?.message || 'Mavzu holati yangilanmadi.');
        }

        if (response.data.overview) {
          applyOverview(response.data.overview);
        } else {
          await loadOverview(true);
        }

        if (response.data.data && response.data.data.topic?.key === selectedTopicKey) {
          setTopicDetail(response.data.data);
        }

        toast.success(response.data.message || 'Mavzu holati yangilandi.');
      } catch (error) {
        toast.error(extractErrorMessage(error, 'Mavzu holatini yangilashda xatolik yuz berdi.'));
      }
    },
    [applyOverview, canManage, jsonRequestConfig, loadOverview, selectedTopicKey]
  );

  const handleQuestionArchive = useCallback(
    async (question, archiveValue) => {
      if (!question || !canManage) {
        return;
      }

      const confirmed = window.confirm(
        archiveValue === 1 ? 'Savolni arxivlashni xohlaysizmi?' : 'Savolni qayta ochishni xohlaysizmi?'
      );

      if (!confirmed) {
        return;
      }

      try {
        const response = await axios.post(
          QUESTION_ARCHIVE_ENDPOINT,
          {
            test_savol_id: question.id,
            arxiv: archiveValue
          },
          jsonRequestConfig
        );

        if (!response.data?.success) {
          throw new Error(response.data?.message || 'Savol holati yangilanmadi.');
        }

        if (response.data.overview) {
          applyOverview(response.data.overview);
        } else {
          await loadOverview(true);
        }

        if (response.data.data) {
          setTopicDetail(response.data.data);
        } else if (selectedTopicKey) {
          await loadTopic(selectedTopicKey);
        }

        toast.success(response.data.message || 'Savol holati yangilandi.');
      } catch (error) {
        toast.error(extractErrorMessage(error, 'Savol holatini yangilashda xatolik yuz berdi.'));
      }
    },
    [applyOverview, canManage, jsonRequestConfig, loadOverview, loadTopic, selectedTopicKey]
  );

  const topicColumns = useMemo(
    () => [
      {
        dataField: 'title',
        text: 'Mavzu',
        sort: true,
        formatter: (value, row) => (
          <button type="button" className="med-testlab-topic-open" onClick={() => openTopicModal(row)}>
            <strong>{value}</strong>
            <small>{row.key}</small>
          </button>
        )
      },
      {
        dataField: 'question_count',
        text: 'Testlar',
        sort: true,
        formatter: (value, row) => (
          <div className="med-testlab-topic-metric">
            <strong>{Number(value || 0) + Number(row.archived_count || 0)}</strong>
            <small>
              {value} faol / {row.archived_count} arxiv
            </small>
          </div>
        ),
        headerStyle: { width: '10rem' }
      },
      {
        dataField: 'image_count',
        text: 'Rasmli',
        sort: true,
        formatter: value => <span className="med-testlab-inline-pill">{value} ta</span>,
        headerStyle: { width: '8rem' }
      },
      {
        dataField: 'preview',
        text: 'Qisqacha',
        formatter: value => <span className="text-600">{value || 'Savollar hali kiritilmagan'}</span>
      },
      {
        dataField: 'is_archived',
        text: 'Holat',
        formatter: (value, row) => {
          const meta = topicStatusMeta(row);
          return <span className={meta.badgeClass}>{meta.label}</span>;
        },
        headerStyle: { width: '9rem' }
      },
      {
        dataField: 'last_updated',
        text: 'Yangilangan',
        sort: true,
        headerStyle: { width: '11rem' }
      },
      {
        dataField: 'actions',
        text: 'Amallar',
        formatter: (value, row) => (
          <div className="med-testlab-table-actions">
            <Button color="light" size="sm" onClick={() => openTopicModal(row)}>
              <FontAwesomeIcon icon="eye" className="mr-1" />
              Kirish
            </Button>
            {canManage ? (
              <Button
                color={row.is_archived ? 'success' : 'warning'}
                size="sm"
                onClick={() => handleTopicArchive(row, row.is_archived ? 0 : 1)}
              >
                <FontAwesomeIcon icon={row.is_archived ? 'reply' : 'archive'} className="mr-1" />
                {row.is_archived ? 'Ochish' : 'Arxiv'}
              </Button>
            ) : null}
          </div>
        ),
        headerStyle: { width: '15rem' }
      }
    ],
    [canManage, handleTopicArchive, openTopicModal]
  );

  const questionColumns = useMemo(
    () => [
      {
        dataField: 'order',
        text: '#',
        sort: true,
        headerStyle: { width: '4.5rem' }
      },
      {
        dataField: 'question_text',
        text: 'Savol',
        sort: true,
        formatter: (value, row) => (
          <div className="med-testlab-table-copy">
            <strong>{value || 'Rasmli savol'}</strong>
            <div className="med-testlab-table-copy__badges">
              {row.has_image ? <span className="med-testlab-inline-pill">Rasmli</span> : null}
              <small>{row.author || 'Muallif ko`rsatilmagan'}</small>
            </div>
          </div>
        )
      },
      {
        dataField: 'correct_answer',
        text: 'To`g`ri javob',
        sort: true,
        headerStyle: { width: '13rem' }
      },
      {
        dataField: 'options',
        text: 'Variantlar',
        formatter: value => (
          <div className="med-testlab-options-stack">
            {(value || []).map((option, index) => (
              <span key={`${option}-${index}`} className="med-testlab-option-chip">
                {option}
              </span>
            ))}
          </div>
        ),
        headerStyle: { width: '18rem' }
      },
      {
        dataField: 'archived',
        text: 'Holat',
        formatter: value => {
          const meta = questionStatusMeta(value);
          return <span className={meta.badgeClass}>{meta.label}</span>;
        },
        headerStyle: { width: '8rem' }
      },
      {
        dataField: 'created_at',
        text: 'Yangilangan',
        sort: true,
        headerStyle: { width: '11rem' }
      },
      {
        dataField: 'actions',
        text: 'Amallar',
        formatter: (value, row) => (
          <div className="med-testlab-table-actions">
            <Button color="light" size="sm" onClick={() => startEditQuestion(row)}>
              <FontAwesomeIcon icon="pencil-alt" className="mr-1" />
              Tahrirlash
            </Button>
            {canManage ? (
              <Button
                color={row.archived ? 'success' : 'warning'}
                size="sm"
                onClick={() => handleQuestionArchive(row, row.archived ? 0 : 1)}
              >
                <FontAwesomeIcon icon={row.archived ? 'reply' : 'archive'} className="mr-1" />
                {row.archived ? 'Ochish' : 'Arxiv'}
              </Button>
            ) : null}
          </div>
        ),
        headerStyle: { width: '15rem' }
      }
    ],
    [canManage, handleQuestionArchive, startEditQuestion]
  );

  const isEditingQuestion = Boolean(questionForm.test_savol_id);
  const activeTopicStatus = topicStatusMeta(activeTopic);
  const canEditQuestions = Boolean(canManage && activeTopic && !activeTopic.is_archived);
  const activeTopicMetrics = useMemo(
    () =>
      activeTopic
        ? [
            {
              key: 'active',
              icon: 'stream',
              label: 'Faol',
              value: activeTopic.question_count,
              tone: 'primary'
            },
            {
              key: 'archived',
              icon: 'archive',
              label: 'Arxiv',
              value: activeTopic.archived_count,
              tone: 'warning'
            },
            {
              key: 'image',
              icon: 'image',
              label: 'Rasmli',
              value: activeTopic.image_count,
              tone: 'info'
            }
          ]
        : [],
    [activeTopic]
  );

  return (
    <div className="med-testlab-page">
      {banner ? (
        <Alert color={banner.color} className="mb-4">
          {banner.text}
        </Alert>
      ) : null}

      {!permissions.view && !loading ? (
        <Card className="border-0 shadow-sm">
          <CardBody className="med-testlab-empty-state">
            <FontAwesomeIcon icon="lock" />
            <strong>Ruxsat talab qilinadi</strong>
            <span>Bu bo`limni ko`rish uchun sizga test sozlamalari bo`yicha ruxsat kerak.</span>
          </CardBody>
        </Card>
      ) : (
        <Card className="border-0 shadow-sm">
          <CardBody>
            <div className="med-testlab-section-head">
              <div>
                <Badge color="soft-info" pill className="mb-2">
                  Mavzular jadvali
                </Badge>
                <h4>Avval mavzuni yarating, keyin ichiga kiring</h4>
                <p>Qator tanlanganda modal ochiladi va shu modal ichida testlar bilan to`liq ishlaysiz.</p>
              </div>
              {canManage ? (
                <Button color="primary" onClick={() => setCreateTopicModalOpen(true)}>
                  <FontAwesomeIcon icon="plus" className="mr-2" />
                  Mavzu yaratish
                </Button>
              ) : null}
            </div>

            <div className="med-testlab-table-toolbar">
              <Input
                value={topicSearch}
                onChange={event => setTopicSearch(event.target.value)}
                placeholder="Mavzu, kalit yoki qisqacha matn bo`yicha qidiring"
                className="med-testlab-input med-testlab-input--search"
              />
              <Input
                type="select"
                value={topicStatusFilter}
                onChange={event => setTopicStatusFilter(event.target.value)}
                className="med-testlab-input med-testlab-input--compact"
              >
                <option value="all">Barchasi</option>
                <option value="active">Faol</option>
                <option value="archived">Arxiv</option>
              </Input>
            </div>

            {loading ? (
              <div className="text-center py-6">
                <Spinner color="primary" className="mb-3" />
                <div className="text-600">Mavzular yuklanmoqda...</div>
              </div>
            ) : (
              <MedDataTable
                data={filteredTopics}
                columns={topicColumns}
                keyField="key"
                pageSize={20}
                paginationResetKey={`${filteredTopics.length}-${topicSearch}-${topicStatusFilter}`}
                wrapperClasses="table-responsive med-testlab-table-wrap"
                tableClasses="table table-hover fs--1 mb-0 med-dashboard-table med-testlab-table"
              />
            )}
          </CardBody>
        </Card>
      )}

      <Modal isOpen={createTopicModalOpen} toggle={() => setCreateTopicModalOpen(!createTopicModalOpen)} centered>
        <Form onSubmit={handleCreateTopic}>
          <ModalHeader toggle={() => setCreateTopicModalOpen(false)}>Yangi mavzu yaratish</ModalHeader>
          <ModalBody>
            <FormGroup className="mb-0">
              <Label for="topic_draft_title">Mavzu nomi</Label>
              <Input
                id="topic_draft_title"
                value={topicDraftTitle}
                onChange={event => setTopicDraftTitle(event.target.value)}
                placeholder="Masalan: Kasbiy orientatsiya testi"
                className="med-testlab-input"
                disabled={!canManage || creatingTopic}
              />
              <small className="text-500 d-block mt-2">
                Mavzu avval alohida yaratiladi. Testlar keyingi modal ichida shu mavzuga qo`shiladi.
              </small>
            </FormGroup>
          </ModalBody>
          <ModalFooter>
            <Button color="light" onClick={() => setCreateTopicModalOpen(false)} disabled={creatingTopic}>
              Bekor qilish
            </Button>
            <Button color="primary" type="submit" disabled={!canManage || creatingTopic}>
              {creatingTopic ? <Spinner size="sm" className="mr-2" /> : <FontAwesomeIcon icon="check-circle" className="mr-2" />}
              Yaratish
            </Button>
          </ModalFooter>
        </Form>
      </Modal>

      <Modal isOpen={renameTopicModalOpen} toggle={closeRenameTopicModal} centered>
        <Form onSubmit={handleRenameTopic}>
          <ModalHeader toggle={closeRenameTopicModal}>Mavzu nomini tahrirlash</ModalHeader>
          <ModalBody>
            <FormGroup className="mb-0">
              <Label for="rename_topic_title">Mavzu nomi</Label>
              <Input
                id="rename_topic_title"
                value={renameTopicTitle}
                onChange={event => setRenameTopicTitle(event.target.value)}
                placeholder="Mavzu nomini kiriting"
                className="med-testlab-input"
                disabled={!canManage || renamingTopic}
              />
              <small className="text-500 d-block mt-2">
                Nom o`zgarganda shu mavzudagi barcha testlarda yangi mavzu nomi ko`rinadi.
              </small>
            </FormGroup>
          </ModalBody>
          <ModalFooter>
            <Button color="light" onClick={closeRenameTopicModal} disabled={renamingTopic}>
              Bekor qilish
            </Button>
            <Button color="primary" type="submit" disabled={!canManage || renamingTopic}>
              {renamingTopic ? (
                <Spinner size="sm" className="mr-2" />
              ) : (
                <FontAwesomeIcon icon="check-circle" className="mr-2" />
              )}
              Saqlash
            </Button>
          </ModalFooter>
        </Form>
      </Modal>

      <Modal isOpen={topicModalOpen} toggle={closeTopicModal} size="xl" className="med-testlab-modal">
        <ModalHeader toggle={closeTopicModal}>
          {activeTopic ? activeTopic.title : 'Mavzu ichidagi testlar'}
        </ModalHeader>
        <ModalBody className="med-testlab-modal-body">
          {topicModalLoading ? (
            <div className="text-center py-6">
              <Spinner color="primary" className="mb-3" />
              <div className="text-600">Mavzu ichidagi testlar yuklanmoqda...</div>
            </div>
          ) : activeTopic ? (
            <>
              <div className="med-testlab-modal-head">
                <div>
                  <Badge className={activeTopicStatus.badgeClass}>{activeTopicStatus.label}</Badge>
                  <h4 className="mt-2 mb-1">{activeTopic.title}</h4>
                  <p className="mb-0">{activeTopic.preview || 'Savollar hali kiritilmagan'}</p>
                </div>
                <div className="med-testlab-modal-stats">
                  {activeTopicMetrics.map(item => (
                    <div
                      key={item.key}
                      className={classNames('med-testlab-compact-stat', `med-testlab-compact-stat--${item.tone}`)}
                    >
                      <div className="med-testlab-compact-stat__icon">
                        <FontAwesomeIcon icon={item.icon} />
                      </div>
                      <div className="med-testlab-compact-stat__copy">
                        <span>{item.label}</span>
                        <strong>{item.value}</strong>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              <Card className="border-0 med-testlab-modal-panel">
                <CardBody>
                  <div className="med-testlab-section-head">
                    <div>
                      <Badge color="soft-primary" pill className="mb-2">
                        Testlar jadvali
                      </Badge>
                      <h4>Kiritilgan testlar</h4>
                      <p>Tanlangan mavzu uchun barcha testlar shu yerda ko`rinadi. Savol qo`shish alohida modalda ochiladi.</p>
                    </div>
                    <div className="med-testlab-section-head__actions">
                      {canManage ? (
                        <Button color="light" onClick={openRenameTopicModal}>
                          <FontAwesomeIcon icon="pencil-alt" className="mr-2" />
                          Nomini tahrirlash
                        </Button>
                      ) : null}
                      {canManage ? (
                        <Button color="primary" onClick={openNewQuestionModal} disabled={activeTopic.is_archived}>
                          <FontAwesomeIcon icon="plus" className="mr-2" />
                          Test qo`shish
                        </Button>
                      ) : null}
                      {canManage ? (
                        <Button
                          color={activeTopic.is_archived ? 'success' : 'warning'}
                          type="button"
                          onClick={() => handleTopicArchive(activeTopic, activeTopic.is_archived ? 0 : 1)}
                        >
                          <FontAwesomeIcon icon={activeTopic.is_archived ? 'reply' : 'archive'} className="mr-2" />
                          {activeTopic.is_archived ? 'Mavzuni ochish' : 'Mavzuni arxivlash'}
                        </Button>
                      ) : null}
                    </div>
                  </div>

                  {activeTopic.is_archived ? (
                    <Alert color="warning" className="mb-3">
                      Bu mavzu arxivda. Yangi test qo`shish uchun avval mavzuni qayta oching.
                    </Alert>
                  ) : null}

                  <div className="med-testlab-table-toolbar">
                    <Input
                      value={questionSearch}
                      onChange={event => setQuestionSearch(event.target.value)}
                      placeholder="Savol, javob yoki variant bo`yicha qidiring"
                      className="med-testlab-input med-testlab-input--search"
                    />
                    <TablePageSizeSelect value={pageSize} onChange={setPageSize} />
                  </div>

                  <MedDataTable
                    data={filteredQuestions}
                    columns={questionColumns}
                    pageSize={pageSize}
                    paginationResetKey={`${selectedTopicKey}-${questionSearch}-${filteredQuestions.length}`}
                    wrapperClasses="table-responsive med-testlab-table-wrap"
                    tableClasses="table table-hover fs--1 mb-0 med-dashboard-table med-testlab-table"
                  />
                </CardBody>
              </Card>
            </>
          ) : (
            <div className="med-testlab-empty-state med-testlab-empty-state--wide">
              <FontAwesomeIcon icon="book" />
              <strong>Mavzu topilmadi</strong>
              <span>Ro`yxatdan qayta tanlab ko`ring.</span>
            </div>
          )}
        </ModalBody>
        <ModalFooter>
          {canManage && activeTopic ? (
            <Button color="primary" onClick={openNewQuestionModal} disabled={activeTopic.is_archived}>
              <FontAwesomeIcon icon="plus" className="mr-2" />
              Test qo`shish
            </Button>
          ) : null}
          <Button color="light" onClick={closeTopicModal}>
            Yopish
          </Button>
        </ModalFooter>
      </Modal>

      <Modal isOpen={questionModalOpen} toggle={closeQuestionModal} size="lg" className="med-testlab-modal">
        <ModalHeader toggle={closeQuestionModal}>
          {isEditingQuestion ? 'Savolni tahrirlash' : 'Yangi test qo`shish'}
        </ModalHeader>
        <ModalBody className="med-testlab-modal-body">
          {activeTopic ? (
            <Card className="border-0 med-testlab-modal-panel">
              <CardBody>
                <div className="med-testlab-section-head">
                  <div>
                    <Badge color={isEditingQuestion ? 'soft-warning' : 'soft-success'} pill className="mb-2">
                      {isEditingQuestion ? 'Tahrirlash' : 'Test qo`shish'}
                    </Badge>
                    <h4>{activeTopic.title}</h4>
                    <p>
                      Savol qo`shish oynasi alohida modalga ko`chirildi. Shu yerda testni kiriting, saqlagandan keyin
                      ro`yxat orqa modalda yangilanadi.
                    </p>
                    <div className="med-testlab-compact-stats med-testlab-compact-stats--tight">
                      {activeTopicMetrics.map(item => (
                        <div
                          key={`question-${item.key}`}
                          className={classNames('med-testlab-compact-stat', `med-testlab-compact-stat--${item.tone}`)}
                        >
                          <div className="med-testlab-compact-stat__icon">
                            <FontAwesomeIcon icon={item.icon} />
                          </div>
                          <div className="med-testlab-compact-stat__copy">
                            <span>{item.label}</span>
                            <strong>{item.value}</strong>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                  <div className="med-testlab-section-head__actions">
                    {canManage ? (
                      <Button color="light" onClick={openRenameTopicModal}>
                        <FontAwesomeIcon icon="pencil-alt" className="mr-2" />
                        Nomini tahrirlash
                      </Button>
                    ) : null}
                    {uploadingImage ? (
                      <span className="med-testlab-inline-pill">
                        <Spinner size="sm" className="mr-2" />
                        Rasm yuklanmoqda
                      </span>
                    ) : null}
                  </div>
                </div>

                {activeTopic.is_archived ? (
                  <Alert color="warning" className="mb-3">
                    Bu mavzu arxivda. Test qo`shish yoki tahrirlash uchun avval mavzuni qayta oching.
                  </Alert>
                ) : null}

                <Form onSubmit={handleSaveQuestion}>
                  <FormGroup>
                    <Label>Mavzu</Label>
                    <Input value={activeTopic.title} readOnly className="med-testlab-input" />
                  </FormGroup>

                  <FormGroup>
                    <Label>Savol matni</Label>
                    <div className="med-testlab-editor">
                      <QuillEditor
                        ref={editorRef}
                        value={questionForm.question_html}
                        onChange={handleEditorChange}
                        modules={editorModules}
                        formats={QUESTION_EDITOR_FORMATS}
                        readOnly={!canEditQuestions}
                        placeholder="Savolni yozing, kerak bo`lsa rasm joylang va formatlang..."
                      />
                    </div>
                    <small className="text-500 d-block mt-2">
                      Variantlar maksimal 4 ta bo`ladi, lekin har biri alohida ko`rinishdagi blok sifatida kiritiladi.
                    </small>
                  </FormGroup>

                  <input
                    ref={imageInputRef}
                    type="file"
                    accept="image/jpeg,image/png,image/webp,image/gif"
                    className="d-none"
                    onChange={handleEditorImagePick}
                  />

                  <Row>
                    {VARIANT_FIELDS.map(field => (
                      <Col md="6" key={field.name} className="mb-3">
                        <div
                          className={classNames(
                            'med-testlab-variant-card',
                            `med-testlab-variant-card--${field.tone}`
                          )}
                        >
                          <div className="med-testlab-variant-card__head">
                            <span>{field.label}</span>
                            <strong>{field.title}</strong>
                            <small>{field.note}</small>
                          </div>
                          <Input
                            name={field.name}
                            value={questionForm[field.name]}
                            onChange={handleQuestionFormChange}
                            className="med-testlab-input"
                            disabled={!canEditQuestions || savingQuestion}
                          />
                        </div>
                      </Col>
                    ))}
                  </Row>

                  <div className="med-testlab-preview-note mb-3">
                    <FontAwesomeIcon icon="info-circle" />
                    <span>
                      Bir mavzu ichida testlarni cheksiz qo`shishingiz mumkin. Har saqlashdan keyin mavzu ichidagi jadval
                      avtomatik yangilanadi.
                    </span>
                  </div>

                  <div className="med-testlab-form-actions">
                    <Button color="primary" type="submit" disabled={!canEditQuestions || savingQuestion || uploadingImage}>
                      {savingQuestion ? (
                        <Spinner size="sm" className="mr-2" />
                      ) : (
                        <FontAwesomeIcon icon="check-circle" className="mr-2" />
                      )}
                      {isEditingQuestion ? 'Savolni yangilash' : 'Savolni saqlash'}
                    </Button>
                    <Button
                      color="light"
                      type="button"
                      onClick={() => resetQuestionForm(activeTopic.key, activeTopic.title)}
                      disabled={savingQuestion}
                    >
                      Tozalash
                    </Button>
                    <Button color="light" type="button" onClick={closeQuestionModal} disabled={savingQuestion}>
                      Yopish
                    </Button>
                  </div>
                </Form>
              </CardBody>
            </Card>
          ) : (
            <div className="med-testlab-empty-state med-testlab-empty-state--wide">
              <FontAwesomeIcon icon="book" />
              <strong>Mavzu tanlanmagan</strong>
              <span>Avval mavzuni oching, keyin shu oynadan test qo`shing.</span>
            </div>
          )}
        </ModalBody>
      </Modal>
    </div>
  );
};

export default TestBuilderSettings;
